/* ---
	Sticky Add to Cart (premium)

	The bar holds no variant options. It mirrors the hidden [data-js-sticky-atc-source] block
	that lives inside the product page — the theme refreshes that block on every variant change
	like any other [data-update-block] — and it delegates the actual add to cart to the product
	form button, so quantity, line item properties and the ajax cart keep working as they do on
	the product page itself.
--- */

if ( typeof StickyAddToCartBar !== 'function' ) {

	class StickyAddToCartBar extends HTMLElement {

		// what counts as a picked option outside of the theme variant picker
		static SELECTED = 'input[type="radio"]:checked, [role="radio"][aria-checked="true"], [role="option"][aria-selected="true"]';

		// the container a custom built plan control sits in, when it is not a <label>
		static CARD = 'label, [class*="card" i], [class*="plan" i], [class*="bundle" i], [class*="option" i], li';

		// wording that marks the subscribe / one-time toggle
		static SUBSCRIPTION = /auto[\s-]?ship|subscri|recurring|deliver every/i;

		// the bar steps aside once the footer comes into view, in order of preference
		static FOOTER = ['.main-footer', '#shopify-section-footer', 'footer'];

		// room left between the bar and a widget moved out of its way
		static CLEARANCE_GAP = 10;

		// taller than this much of the screen and it is a panel an app opened, not a launcher
		static CLEARANCE_HEIGHT = 0.6;

		// where the bar is asked what is being painted on top of it
		static CLEARANCE_COLUMNS = [0.03, 0.15, 0.5, 0.85, 0.97];
		static CLEARANCE_ROWS = [0.35, 0.8];

		// where the original price hides on an option card. Names vary wildly between apps,
		// so a line-through in the computed style counts as well — see isStruck().
		static COMPARE = 's, del, strike, [class*="compare" i], [class*="was" i], [class*="strike" i],'
			+ '[class*="original" i], [class*="regular" i], [class*="old" i], [class*="crossed" i], [class*="full-price" i]';

		// wording that marks a saving rather than a price
		static SAVING = /save|discount|%/i;

		// the same in a class name, where "discount" is off limits: Appstle names its markup
		// appstle-volume-discount-price, and that is the price itself
		static SAVING_CLASS = /saving|save/i;

		// wording that tells the shopper an amount buys one item out of a set
		static PER_UNIT = /per\s*(?:unit|bottle|item|serving|capsule|pack)|each\b|\/\s*(?:unit|bottle|item|ea)\b/i;

		// how many items an option holds — "Max Savings (6 Bottles)"
		static PACK = /(\d+)\s*(?:bottles?|packs?|items?|units?|caps?|count|ct\b)/i;

		// any option control, used to tell one option's markup from the next
		static CONTROL = 'input[type="radio"], [role="radio"], [role="option"]';

		// how a widget marks its picked option when it uses neither inputs nor aria
		static SELECTED_CLASS = '[class*="selected" i], [class*="active" i], [class*="checked" i], [class*="current" i]';

		// anything the Appstle apps render, whatever they call it this release
		static APPSTLE = '[class*="appstle" i], [id*="appstle" i]';

		// Appstle Subscriptions announces every plan change on the document
		static APPSTLE_EVENTS = [
			'AppstleSubscription:SubscriptionWidget:SellingPlanSelected',
			'AppstleSubscription:SubscriptionWidget:SellingPlanDeSelected',
			'AppstleSubscription:SubscriptionWidget:sellingPlanChanged'
		];

		connectedCallback() {

			if ( this._mounted ) return;

			this._prefix = window.KT_PREFIX || '';
			this.productPage = document.getElementById(this.dataset.productPage);

			if ( ! this.productPage ) return;

			this.mainButton = this.productPage.querySelector('[data-js-product-add-to-cart]');
			this.source = this.productPage.querySelector('[data-js-sticky-atc-source]');

			// no buy buttons block on the page, nothing to stick to
			if ( ! this.mainButton || ! this.source ) {
				this.remove();
				return;
			}

			this._mounted = true;

			this.mainButtonText = this.productPage.querySelector('[data-js-product-add-to-cart-text]');
			this.productForm = this.mainButton.closest('product-form');
			this.content = this.querySelector('[data-js-sticky-atc-content]');
			this.button = this.querySelector('[data-js-sticky-atc-button]');
			this.buttonText = this.querySelector('[data-js-sticky-atc-button-text]');

			// product blocks (theme options, app bundles, subscription widgets) all live here
			this.blocks = this.productPage.querySelector('.product-text') || this.productPage;

			this.subscriptionLabel = this.dataset.subscriptionLabel || 'Auto-Ship';
			this.oneTimeLabel = this.dataset.oneTimeLabel || 'One-time';
			this.moneyPattern = this.buildMoneyPattern();

			if ( this.dataset.priceSelector ) {
				try {
					this.priceSource = this.blocks.querySelector(this.dataset.priceSelector);
				} catch (error) {
					console.error('Sticky add to cart: invalid price selector', error);
				}
			}

			// the bar is fixed, so it has to sit outside of any transformed/overflowing ancestor
			document.querySelectorAll(`sticky-add-to-cart-bar[data-section-id="${this.dataset.sectionId}"]`).forEach(bar => {
				if ( bar !== this ) bar.unmount();
			});
			if ( this.parentElement !== document.body ) document.body.appendChild(this);

			this.watchMoney();
			this.syncContent();
			this.syncButton();

			this._onClick = this.onClick.bind(this);
			this.button.addEventListener('click', this._onClick);

			// the mutation observer below covers the regular flow, this is the safety net for
			// when the product page markup itself gets swapped out
			this.variants = this.productPage.querySelector('product-variants');
			this._onVariantChange = this.refresh.bind(this);
			this.variants?.addEventListener('VARIANT_CHANGE', this._onVariantChange);

			this.watchContent();
			this.watchButton();
			this.watchSelection();
			this.watchScroll();
			this.watchClearance();

		}

		/* ---
			Sync
		--- */

		syncContent() {
			this.content.innerHTML = this.source.innerHTML;
			this.syncSelection();
			this.syncPrice();
		}

		/* ---
			Selection line — theme options come rendered from Liquid, everything else
			(bundles, subscription plans, app blocks) is read from the page itself
		--- */

		syncSelection() {

			const holder = this.content.querySelector('[data-js-sticky-atc-selection]');
			if ( ! holder ) return;

			holder.querySelectorAll('[data-js-sticky-atc-extra]').forEach(part => part.remove());

			const seen = new Set([...holder.children].map(part => part.textContent.trim().toLowerCase()));

			this.collectSelections().forEach(label => {
				const key = label.toLowerCase();
				if ( key === '' || seen.has(key) ) return;
				seen.add(key);
				const part = document.createElement('span');
				part.className = 'sticky-atc__selection-part';
				part.setAttribute('data-js-sticky-atc-extra', '');
				part.textContent = label;
				holder.appendChild(part);
			});

		}

		collectSelections() {

			const labels = [];

			// a selector set in the theme editor takes over — it points straight at the
			// element(s) holding the current selection
			if ( this.dataset.selectionSelector ) {
				try {
					this.blocks.querySelectorAll(this.dataset.selectionSelector).forEach(elm => {
						if ( this.isVisible(elm) ) labels.push(this.readLabel(elm));
					});
				} catch (error) {
					console.error('Sticky add to cart: invalid selection selector', error);
				}
				return labels.filter(Boolean).slice(0, 4);
			}

			this.selectedCards().forEach(card => labels.push(this.readLabel(card)));

			// the purchase type reads as its state, not as its wording — the checkbox says
			// something like "Auto-Ship & Save Even More", the bar has room for one word
			const subscription = this.subscriptionToggle();
			if ( subscription !== null ) {
				labels.push(subscription ? this.subscriptionLabel : this.oneTimeLabel);
			}

			return labels.filter(Boolean).slice(0, 4);

		}

		// the picked plan / bundle cards: a checked radio, or an aria driven card for the
		// widgets that build their own controls
		selectedCards() {

			const cards = [...this.blocks.querySelectorAll(StickyAddToCartBar.SELECTED)]
				.filter(elm => {
					if ( elm.closest('product-variants') || elm.closest('[data-js-sticky-atc-source]') ) return false;
					if ( ['id', 'quantity'].includes(elm.name) ) return false;
					// the plan control is not an option card: it already reads as Auto-Ship
					if ( (elm.name || '').includes('selling_plan') ) return false;
					// helper markup the theme and apps keep off screen holds no selection
					return this.isVisible(elm);
				})
				.map(elm => {
					if ( ! (elm instanceof HTMLInputElement) ) return elm;
					return this.labelFor(elm) || elm.closest(StickyAddToCartBar.CARD) || elm.parentElement;
				})
				.filter(Boolean);

			return cards.length > 0 ? cards : this.selectedByClass();

		}

		// last resort: a widget that marks its choice with a class and nothing else
		selectedByClass() {

			return [...this.blocks.querySelectorAll(StickyAddToCartBar.SELECTED_CLASS)]
				.filter(elm => {

					if ( elm.closest('product-variants') || ! this.isVisible(elm) ) return false;

					// it has to be one option among several, not a lone decoration
					const siblings = elm.parentElement ? [...elm.parentElement.children] : [];

					return siblings.filter(sibling => sibling.tagName === elm.tagName).length > 1;

				})
				.slice(0, 2);

		}

		// null when the product has no subscribe option at all, otherwise its state
		subscriptionToggle() {

			// whatever the widget looks like, a selling plan is what actually reaches the cart —
			// Appstle Subscriptions keeps it in .appstle_sub_widget input[name=selling_plan]
			const plan = this.sellingPlan();
			if ( plan !== null ) return plan;

			const checkbox = [...this.blocks.querySelectorAll('input[type="checkbox"]')].find(input => {
				if ( ! this.isVisible(input) ) return false;
				const label = this.labelFor(input);
				return label !== null && StickyAddToCartBar.SUBSCRIPTION.test(label.textContent);
			});

			if ( checkbox ) return checkbox.checked;

			// widgets that build their own control instead of using an input
			const custom = [...this.blocks.querySelectorAll('[role="checkbox"], [role="switch"]')].find(elm => {
				return this.isVisible(elm) && StickyAddToCartBar.SUBSCRIPTION.test(elm.textContent);
			});

			return custom ? custom.getAttribute('aria-checked') === 'true' : null;

		}

		// true on a subscription, false on one-time, null when the product has no plans
		sellingPlan() {

			const inputs = [...this.productPage.querySelectorAll('[name="selling_plan"]')];
			if ( inputs.length === 0 ) return null;

			const picked = inputs.filter(input => ['radio', 'checkbox'].includes(input.type));

			if ( picked.length > 0 ) {
				const checked = picked.find(input => input.checked);
				return checked ? checked.value.trim() !== '' : false;
			}

			const input = inputs[0];
			const value = input instanceof HTMLSelectElement ? input.selectedOptions[0]?.value : input.value;

			return String(value || '').trim() !== '';

		}

		readLabel(element) {

			if ( element.dataset.stickyAtcSelection ) return this.clean(element.dataset.stickyAtcSelection);

			let source = element;

			// a selector may point at the control rather than at the card around it
			if ( element instanceof HTMLInputElement ) {
				source = this.labelFor(element) || element.closest(StickyAddToCartBar.CARD) || element.parentElement;
			}

			if ( ! source ) return '';

			// plan cards wrap a price, a saving and a blurb — take the element holding the name
			const title =
				source.querySelector?.('[data-sticky-atc-selection]') ||
				source.querySelector?.('h1, h2, h3, h4, h5, h6') ||
				source.querySelector?.('strong, b') ||
				source.querySelector?.('[class*="title" i], [class*="name" i], [class*="heading" i]');

			return this.clean(title?.textContent || source.textContent || '');

		}

		labelFor(element) {

			return (
				( element.id ? this.blocks.querySelector(`label[for="${CSS.escape(element.id)}"]`) : null ) ||
				element.closest('label') ||
				element.parentElement?.querySelector('label') ||
				null
			);

		}

		isVisible(element) {

			// visually hidden inputs behind styled labels still count, hidden helper markup does not
			if ( typeof element.checkVisibility === 'function' ) return element.checkVisibility();

			return element.offsetParent !== null || getComputedStyle(element).position === 'fixed';

		}

		clean(value) {
			const label = value.replace(/\s+/g, ' ').trim();
			return label.length > 48 ? `${label.slice(0, 47).trim()}…` : label;
		}

		/* ---
			Price — mirrors a price element of the product page when one is configured, so
			app calculated prices (bundles, plans) stay in step
		--- */

		syncPrice() {

			const holder = this.content.querySelector('[data-js-sticky-atc-price]');
			if ( ! holder ) return;

			if ( ! this.priceSource ) {

				// the price the customer reads on the page wins, since an app may work out a
				// bundle or plan price the variant knows nothing about
				// the picked option first: it is the one place that can only ever describe
				// what the customer chose
				const option = this.optionPrice() || this.learnedPrice() || this.appstlePrice() || this.pagePrice();

				if ( option ) {
					this.renderPrice(holder, option.current, option.compare, option.unit);
				} else {
					this.renderTotal(holder);
				}

				this.debug(option);

				return;

			}

			// the bar sits outside of the product page, so state driven rules (a price shown
			// only once a variant is picked, for instance) no longer apply to the copy —
			// leave behind whatever the product page is hiding right now
			const clone = this.priceSource.cloneNode(true);
			const copies = clone.querySelectorAll('*');

			this.priceSource.querySelectorAll('*').forEach((elm, index) => {
				if ( getComputedStyle(elm).display === 'none' ) copies[index]?.remove();
			});

			holder.innerHTML = clone.innerHTML;

		}

		/* ---
			Whichever amount on the page reacts to the options is the price, whatever markup
			the app wrapped it in. The first change teaches the bar where to look.
		--- */

		watchMoney() {

			const shown = new Map();

			this.blocks.querySelectorAll('*').forEach(elm => {
				if ( elm.children.length > 0 ) return;
				const money = this.moneyIn(elm);
				if ( money !== '' ) shown.set(elm, money);
			});

			if ( this._money ) {

				const changed = [...shown].filter(([elm, money]) => this._money.has(elm) && this._money.get(elm) !== money);

				// subscribing re-prices every option at once, so a changed amount only says
				// something about the price if it is not sitting on an option nobody picked
				const price = changed.find(([elm]) => ! this.isCompare(elm) && ! this.isSaving(elm) && ! this.foreignOption(elm));

				if ( price ) {
					this._priceNode = price[0];
					this._compareNode = changed.map(([elm]) => elm).find(elm => this.isCompare(elm)) || null;
				}

			}

			this._money = shown;

		}

		// every option card on the page, picked or not
		optionContainers() {

			const controls = [...this.blocks.querySelectorAll(StickyAddToCartBar.CONTROL)].filter(control => {
				if ( control.closest('product-variants') || control.closest('[data-js-sticky-atc-source]') ) return false;
				return ! (control.name || '').includes('selling_plan');
			});

			if ( controls.length > 0 ) {
				return controls
					.map(control => control instanceof HTMLInputElement
						? this.labelFor(control) || control.closest(StickyAddToCartBar.CARD) || control.parentElement
						: control)
					.filter(Boolean);
			}

			// a widget built out of plain elements: the picked card's siblings are the options
			const picked = this.selectedCards()[0];

			return picked && picked.parentElement ? [...picked.parentElement.children] : [];

		}

		foreignOption(element) {

			const picked = this.selectedCards();

			return this.optionContainers().some(container => {
				if ( ! container.contains(element) ) return false;
				return ! picked.some(card => card === container || card.contains(container) || container.contains(card));
			});

		}

		learnedPrice() {

			if ( ! this._priceNode || ! this.blocks.contains(this._priceNode) ) return null;
			if ( this.foreignOption(this._priceNode) ) return null;

			const current = this.moneyIn(this._priceNode);
			if ( current === '' ) return null;

			return {
				current: current,
				compare: this._compareNode && this.blocks.contains(this._compareNode) ? this.moneyIn(this._compareNode) : '',
				unit: this.unitLabel(this._priceNode, this.blocks)
			};

		}

		isCompare(element) {
			return this.isStruck(element, this.blocks);
		}

		// its own words only: an app names its markup things like "volume-discount-price",
		// so a discount in the class or in a child is no reason to pass the price over
		isSaving(element) {
			return StickyAddToCartBar.SAVING.test(this.ownText(element)) || StickyAddToCartBar.SAVING_CLASS.test(element.className || '');
		}

		ownText(element) {
			return [...element.childNodes]
				.filter(node => node.nodeType === Node.TEXT_NODE)
				.map(node => node.nodeValue)
				.join(' ');
		}

		debug(price) {

			if ( ! window.STICKY_ATC_DEBUG && ! location.search.includes('sticky_atc_debug') ) return;

			console.log('[sticky atc]', {
				selection: this.collectSelections(),
				price: price,
				source: this.priceSource ? 'price selector setting' : this._priceNode ? 'learned from the page' : price ? 'read off the option' : 'variant price',
				cards: this.selectedCards(),
				priceNode: this._priceNode
			});

		}

		// the money shown on the picked option card, exactly as the product page shows it
		optionPrice() {

			for ( const selected of this.selectedCards() ) {

				const price = this.scopePrice(this.priceScope(selected));

				if ( price ) {
					if ( this.packSize(selected) === 1 ) price.unit = '';
					return price;
				}

			}

			return null;

		}

		// the first amount inside a piece of the page, with the savings pitch and the
		// compare-at price left out. Verbatim — whatever it reads is what the bar repeats.
		scopePrice(scope) {

			if ( ! scope ) return null;

			const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
			let current = '';
			let compare = '';
			let unit = '';

			while ( walker.nextNode() ) {

				const text = walker.currentNode;
				const money = this.moneyIn(text);

				if ( money === '' ) continue;
				if ( StickyAddToCartBar.SAVING.test(text.nodeValue) ) continue;
				if ( this.inSavingContext(text.parentElement, scope) ) continue;

				if ( this.isStruck(text.parentElement, scope) ) {
					if ( compare === '' ) compare = money;
				} else if ( current === '' ) {
					current = money;
					unit = this.unitLabel(text.parentElement, scope);
				}

				if ( current !== '' && compare !== '' ) break;

			}

			// an original costs more than the price it is struck out for; anything smaller
			// that got this far is a saving, not a price
			if ( compare !== '' && this.amount(compare) <= this.amount(current) ) compare = '';

			return current === '' ? null : { current: current, compare: compare, unit: unit };

		}

		// "Per Unit", in the shop's own words, when the option prices a set one item at a time
		unitLabel(element, scope) {

			let node = element;
			let depth = 0;

			while ( node && node.nodeType === Node.ELEMENT_NODE && depth < 3 ) {

				const phrase = (node.textContent || '').replace(/\s+/g, ' ').trim();
				const match = phrase.length <= 48 ? phrase.match(StickyAddToCartBar.PER_UNIT) : null;

				if ( match ) return match[0].trim();
				if ( node === scope ) break;

				node = node.parentElement;
				depth++;

			}

			return '';

		}

		// a single bottle is priced by the bottle either way, so the wording only earns its
		// place in the bar once the option holds more than one
		packSize(card) {

			const pack = (card.textContent || '').match(StickyAddToCartBar.PACK);

			return pack ? parseInt(pack[1], 10) : 0;

		}

		// "Save" and the amount often live in separate elements, so the wording has to be
		// looked for above the number as well as beside it
		inSavingContext(element, scope) {

			let node = element;
			let depth = 0;

			while ( node && node.nodeType === Node.ELEMENT_NODE && depth < 4 ) {

				if ( this.isSaving(node) ) return true;

				// a short phrase around the number is a badge like "Save $37.51"; a long one
				// is the whole card, where a savings line elsewhere says nothing about it
				const phrase = (node.textContent || '').replace(/\s+/g, ' ').trim();
				if ( phrase.length <= 48 && StickyAddToCartBar.SAVING.test(phrase) ) return true;

				if ( node === scope ) break;

				node = node.parentElement;
				depth++;

			}

			return false;

		}

		amount(money) {

			const digits = money.replace(/[^\d.,]/g, '').replace(/[.,](?=\d{3}\b)/g, '').replace(',', '.');

			return parseFloat(digits) || 0;

		}

		// struck through by markup or by stylesheet, either way it is the original price
		isStruck(element, scope) {

			let node = element;

			while ( node && node.nodeType === Node.ELEMENT_NODE ) {

				if ( node.matches(StickyAddToCartBar.COMPARE) ) return true;
				if ( (getComputedStyle(node).textDecorationLine || '').includes('line-through') ) return true;
				if ( node === scope ) break;

				node = node.parentElement;

			}

			return false;

		}

		// Appstle prices the bundle and the plan, so its widget holds the number the
		// customer is actually being charged
		appstlePrice() {

			for ( const widget of this.blocks.querySelectorAll(StickyAddToCartBar.APPSTLE) ) {

				if ( ! this.isVisible(widget) ) continue;

				const selected = widget.querySelector(StickyAddToCartBar.SELECTED) || widget.querySelector(StickyAddToCartBar.SELECTED_CLASS);
				const price = this.scopePrice(selected ? this.priceScope(selected) : widget);

				if ( price ) return price;

			}

			return null;

		}

		// before anything has been clicked, a widget that prices the whole basket in one place
		// rather than on each option still has that total on the page
		pagePrice() {

			const candidates = this.blocks.querySelectorAll('[class*="total" i], [class*="price" i], [id*="total" i]');

			for ( const elm of candidates ) {

				if ( elm.closest('[data-js-sticky-atc-source]') || elm.closest('product-variants') ) continue;
				if ( ! this.isVisible(elm) || this.isSaving(elm) || this.isCompare(elm) ) continue;
				if ( this.foreignOption(elm) ) continue;

				const current = this.moneyIn(elm);
				if ( current === '' ) continue;

				const scope = elm.parentElement || elm;
				const struck = [...scope.querySelectorAll(StickyAddToCartBar.COMPARE)].find(node => this.moneyIn(node) !== '');
				const compare = this.moneyIn(struck);

				return { current: current, compare: compare === current ? '' : compare };

			}

			return null;

		}

		// a card often holds only the name, with the price sitting in a column beside it —
		// climb until money turns up, stopping before the next option is swallowed
		priceScope(card) {

			let scope = card;

			while ( scope && this.moneyIn(scope) === '' ) {

				const parent = scope.parentElement;

				if ( ! parent || parent === this.blocks || parent === this.productPage ) break;
				if ( parent.querySelectorAll(StickyAddToCartBar.CONTROL).length > 1 ) break;

				scope = parent;

			}

			return scope || card;

		}

		moneyIn(node) {

			if ( ! node ) return '';

			const match = (node.textContent || '').replace(/\s+/g, ' ').match(this.moneyPattern);

			return match ? match[0].trim() : '';

		}

		renderPrice(holder, current, compare, unit) {

			const price = document.createElement('span');
			price.className = 'sticky-atc__price-current';
			price.textContent = current;

			holder.textContent = '';
			holder.appendChild(price);

			if ( unit ) {
				const per = document.createElement('span');
				per.className = 'sticky-atc__price-per';
				per.textContent = unit;
				holder.appendChild(per);
			}

			if ( compare ) {
				const was = document.createElement('s');
				was.className = 'sticky-atc__price-compare';
				was.textContent = compare;
				holder.appendChild(was);
			}

		}

		// bundle widgets buy the same variant several times over, so the bar prices the line,
		// not the bottle — 6 × $29.99 reads as $179.94
		renderTotal(holder) {

			const unit = parseInt(holder.dataset.unitPrice, 10);
			if ( isNaN(unit) ) return;

			const quantity = this.quantity();
			const compare = parseInt(holder.dataset.unitCompare, 10);

			const current = holder.querySelector('.sticky-atc__price-current');
			if ( current ) current.textContent = this.money(unit * quantity);

			const was = holder.querySelector('.sticky-atc__price-compare');
			if ( was && ! isNaN(compare) ) was.textContent = this.money(compare * quantity);

		}

		quantity() {

			const input = this.blocks.querySelector('input[name="quantity"]');
			const quantity = input ? parseInt(input.value, 10) : 1;

			return quantity > 0 ? quantity : 1;

		}

		// matches an amount written the way this shop writes them, "$179.94" or "179,94 kr"
		buildMoneyPattern() {

			const format = window.KROWN?.settings?.shop_money_format || '${{amount}}';
			const escape = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			const [prefix, suffix] = format.split(/\{\{.*?\}\}/).map(part => (part || '').replace(/<[^>]*>/g, '').trim());
			const number = '\\d[\\d., \' ]*\\d|\\d';

			return new RegExp(
				`${prefix ? `${escape(prefix)}\\s?` : ''}(?:${number})${suffix ? `\\s?${escape(suffix)}` : ''}`
			);

		}

		money(cents) {

			const format = window.KROWN?.settings?.shop_money_format || '${{amount}}';
			const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);

			if ( ! placeholder ) return format;

			const rules = {
				amount: [2, ',', '.'],
				amount_no_decimals: [0, ',', '.'],
				amount_with_comma_separator: [2, '.', ','],
				amount_no_decimals_with_comma_separator: [0, '.', ','],
				amount_no_decimals_with_space_separator: [0, ' ', '.'],
				amount_with_apostrophe_separator: [2, "'", '.']
			};

			const [precision, thousands, decimal] = rules[placeholder[1]] || rules.amount;
			const parts = (cents / 100).toFixed(precision).split('.');
			const amount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (parts[1] ? decimal + parts[1] : '');

			return format.replace(placeholder[0], amount) + (window.KROWN?.settings?.iso_code || '');

		}

		refresh() {

			if ( ! this.productPage.contains(this.source) ) {
				const source = this.productPage.querySelector('[data-js-sticky-atc-source]');
				if ( source ) {
					this.source = source;
					this._contentObserver?.disconnect();
					this.watchContent();
				}
			}

			this.syncContent();
			this.syncButton();

		}

		syncButton() {

			const disabled = this.mainButton.disabled || this.mainButton.classList.contains(`${this._prefix}disabled`);

			this.button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
			this.button.classList.toggle('is-working', this.mainButton.classList.contains(`${this._prefix}working`));

			if ( this.mainButtonText ) {
				const label = this.mainButtonText.textContent.trim();
				if ( label !== '' && label !== this.buttonText.textContent ) this.buttonText.textContent = label;
			}

		}

		watchContent() {
			this._contentObserver = new MutationObserver(this.syncContent.bind(this));
			this._contentObserver.observe(this.source, { childList: true, subtree: true, characterData: true });
		}

		watchSelection() {

			// a timer rather than an animation frame: app widgets can rewrite their markup
			// while the tab is in the background, where frames never come
			this._onSelectionChange = () => {
				clearTimeout(this._selectionTimer);
				this._selectionTimer = setTimeout(() => {
					this.watchMoney();
					this.syncSelection();
					this.syncPrice();
				}, 50);
			};

			// app widgets can swap classes instead of firing events, and the theme quantity
			// buttons write straight to the input — so watch everything cheap
			this.blocks.addEventListener('change', this._onSelectionChange);
			this.blocks.addEventListener('input', this._onSelectionChange);
			this.blocks.addEventListener('click', this._onSelectionChange);

			StickyAddToCartBar.APPSTLE_EVENTS.forEach(name => document.addEventListener(name, this._onSelectionChange));
			this._selectionObserver = new MutationObserver(this._onSelectionChange);
			this._selectionObserver.observe(this.blocks, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ['class', 'checked', 'selected', 'aria-checked', 'aria-selected', 'data-selected']
			});

			if ( this.priceSource ) {
				this._priceObserver = new MutationObserver(this.syncPrice.bind(this));
				this._priceObserver.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
			}

		}

		watchButton() {
			this._buttonObserver = new MutationObserver(this.syncButton.bind(this));
			this._buttonObserver.observe(this.mainButton, {
				attributes: true,
				attributeFilter: ['class', 'disabled'],
				childList: true,
				subtree: true,
				characterData: true
			});
		}

		/* ---
			Visibility — the bar shows once the product form button is scrolled past, and
			steps back down when the footer is reached so it never covers it
		--- */

		watchScroll() {

			const anchor = this.productForm || this.mainButton;
			this.footer = this.findFooter();

			this._onScroll = () => {
				const viewport = window.innerHeight || document.documentElement.clientHeight;
				const pastButton = anchor.getBoundingClientRect().bottom < 0;
				const atFooter = this.footer !== null && this.footer.getBoundingClientRect().top <= viewport;
				this.toggle(pastButton && ! atFooter);
			};

			window.addEventListener('scroll', this._onScroll, { passive: true });
			window.addEventListener('resize', this._onScroll, { passive: true });
			this._onScroll();

		}

		findFooter() {

			for ( const selector of StickyAddToCartBar.FOOTER ) {
				const footer = document.querySelector(selector);
				if ( footer ) return footer;
			}

			return null;

		}

		toggle(visible) {
			if ( visible === this._visible ) return;
			this._visible = visible;
			this.classList.toggle('is-visible', visible);
			this.setAttribute('aria-hidden', visible ? 'false' : 'true');
			this._onClearance?.();
		}

		/* ---
			Clearance — chat launchers, discount badges and the like are pinned to the bottom of
			the screen by their app, which is where the bar now is. They are moved up out of its
			way for as long as it is on screen, rather than buried under it with a z-index: a
			shopper can still reach them, and the bar stays below the theme's own drawers.

			Anything that should stay put can be marked [data-sticky-atc-ignore]. The height of
			the bar is published as --sticky-atc-height on the document, for placing app widgets
			by hand.
		--- */

		watchClearance() {

			this._lifted = new Map();

			this._onClearance = () => {
				clearTimeout(this._clearanceTimer);
				this._clearanceTimer = setTimeout(this.syncClearance.bind(this), 100);
			};

			// app widgets mount late, and keep mounting long after the page has settled
			this._clearanceObserver = new MutationObserver(this._onClearance);
			this._clearanceObserver.observe(document.body, { childList: true, subtree: true });

			// the bar is not a fixed height: it wraps to two lines on a small screen
			if ( typeof ResizeObserver === 'function' ) {
				this._sizeObserver = new ResizeObserver(this._onClearance);
				this._sizeObserver.observe(this);
			}

			// the bar slides in, so where it ends up is only true once it has stopped moving
			this._onTransitionEnd = event => { if ( event.target === this ) this.syncClearance(); };
			this.addEventListener('transitionend', this._onTransitionEnd);

			window.addEventListener('resize', this._onClearance, { passive: true });

			this.syncClearance();

		}

		syncClearance() {

			const viewport = window.innerHeight || document.documentElement.clientHeight;
			const height = this.offsetHeight;

			// where the bar sits once it has finished sliding in, which is what a widget has to
			// clear — its current rectangle mid animation would move everything twice
			const top = this._visible ? viewport - height : viewport;

			document.documentElement.style.setProperty('--sticky-atc-height', `${this._visible ? height : 0}px`);

			new Set([...this._lifted.keys(), ...this.clearanceTargets(top, viewport)]).forEach(widget => {

				if ( ! widget.isConnected ) {
					this._lifted.delete(widget);
					return;
				}

				const lifted = this._lifted.get(widget)?.distance || 0;
				const rect = widget.getBoundingClientRect();

				// an expanded panel is no longer a launcher: leave it where its app put it
				const overlap = rect.height > viewport * StickyAddToCartBar.CLEARANCE_HEIGHT
					? 0
					: rect.bottom + lifted - top;

				this.lift(widget, overlap > 0 ? Math.round(overlap + StickyAddToCartBar.CLEARANCE_GAP) : 0);

			});

		}

		// what is being painted over the bar right now, asked of the browser rather than
		// guessed from a list of app names that would go stale
		clearanceTargets(top, viewport) {

			if ( ! this._visible || typeof document.elementsFromPoint !== 'function' ) return [];

			const width = window.innerWidth || document.documentElement.clientWidth;
			const targets = [];

			StickyAddToCartBar.CLEARANCE_ROWS.forEach(row => {

				const y = Math.round(top + this.offsetHeight * row);
				if ( y < 0 || y >= viewport ) return;

				StickyAddToCartBar.CLEARANCE_COLUMNS.forEach(column => {

					const x = Math.min(width - 1, Math.round(width * column));

					document.elementsFromPoint(x, y).forEach(elm => {
						const target = this.floatRoot(elm, viewport);
						if ( target && ! targets.includes(target) ) targets.push(target);
					});

				});

			});

			return targets;

		}

		// the element an app pinned to the screen, climbing from whatever was hit. When that
		// turns out to fill the screen it is a panel the widget opened, and the piece of it
		// sitting over the bar is moved instead.
		floatRoot(element, viewport) {

			if ( element === this || this.contains(element) ) return null;
			if ( element.closest('[data-sticky-atc-ignore]') ) return null;

			let node = element;
			let inner = null;

			while ( node && node.nodeType === Node.ELEMENT_NODE && node !== document.body ) {

				const large = node.getBoundingClientRect().height > viewport * StickyAddToCartBar.CLEARANCE_HEIGHT;

				if ( getComputedStyle(node).position === 'fixed' ) return large ? inner : node;
				if ( ! large ) inner = node;

				node = node.parentElement;

			}

			// nothing pinned it to the screen, so it is page content the bar scrolls over
			return null;

		}

		// translate rather than bottom / margin: it composes with whatever the app already does
		// to the element instead of overwriting it, and it survives their !important rules
		lift(widget, distance) {

			const state = this._lifted.get(widget);

			if ( (state?.distance || 0) === distance ) return;

			if ( distance === 0 ) {
				if ( state?.original ) widget.style.setProperty(state.property, state.original);
				else if ( state ) widget.style.removeProperty(state.property);
				this._sizeObserver?.unobserve(widget);
				this._lifted.delete(widget);
				return;
			}

			// a launcher that grows into a panel has to be handed straight back to its app
			if ( ! state ) this._sizeObserver?.observe(widget);

			const property = state?.property || (CSS.supports('translate', '0px') ? 'translate' : 'transform');

			this._lifted.set(widget, {
				distance: distance,
				property: property,
				original: state ? state.original : widget.style.getPropertyValue(property)
			});

			widget.style.setProperty(
				property,
				property === 'translate' ? `0 -${distance}px` : `translateY(-${distance}px)`,
				'important'
			);

		}

		/* ---
			Add to cart — always goes through the product form button
		--- */

		onClick() {

			if ( this.button.getAttribute('aria-disabled') === 'true' ) {
				// nothing picked yet: send the customer to the options instead of failing silently
				const variants = this.productPage.querySelector('product-variants');
				if ( variants && ! this.productPage.classList.contains(`${this._prefix}variant-selected`) ) {
					variants.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
				return;
			}

			this.button.classList.add('is-working');
			this.mainButton.click();

		}

		/* ---
			Teardown (theme editor)
		--- */

		unmount() {
			this._contentObserver?.disconnect();
			this._buttonObserver?.disconnect();
			this._selectionObserver?.disconnect();
			this._priceObserver?.disconnect();
			this._clearanceObserver?.disconnect();
			this._sizeObserver?.disconnect();
			clearTimeout(this._selectionTimer);
			clearTimeout(this._clearanceTimer);
			// hand every widget that was moved out of the way back to its app
			[...(this._lifted?.keys() || [])].forEach(widget => this.lift(widget, 0));
			document.documentElement.style.removeProperty('--sticky-atc-height');
			if ( this._onTransitionEnd ) this.removeEventListener('transitionend', this._onTransitionEnd);
			if ( this._onClearance ) window.removeEventListener('resize', this._onClearance);
			if ( this._onSelectionChange ) {
				['change', 'input', 'click'].forEach(type => this.blocks?.removeEventListener(type, this._onSelectionChange));
				StickyAddToCartBar.APPSTLE_EVENTS.forEach(name => document.removeEventListener(name, this._onSelectionChange));
			}
			if ( this._onScroll ) {
				window.removeEventListener('scroll', this._onScroll);
				window.removeEventListener('resize', this._onScroll);
			}
			if ( this._onClick ) this.button?.removeEventListener('click', this._onClick);
			if ( this._onVariantChange ) this.variants?.removeEventListener('VARIANT_CHANGE', this._onVariantChange);
			this._mounted = false;
			this.remove();
		}

	}

	if ( typeof customElements.get('sticky-add-to-cart-bar') == 'undefined' ) {
		customElements.define('sticky-add-to-cart-bar', StickyAddToCartBar);
	}

	document.addEventListener('shopify:section:unload', e => {
		if ( e.target.classList.contains('mount-product-page') ) {
			const sectionId = e.detail?.sectionId;
			document.querySelectorAll('sticky-add-to-cart-bar').forEach(bar => {
				if ( ! sectionId || bar.dataset.sectionId === sectionId ) bar.unmount();
			});
		}
	});

}
